import * as React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { range } from '@/utils';
import LazyVideoGif from '@/components/VideoGif/LazyVideoGif';

import StraightArrow from './StraightArrow';

const MOBILE_CUTOFF = 650;

const NUM_TO_SHOW = 7;

const CHOICE_HEIGHT = 3;
const CHOICE_GAP = 0.5;

const TOTAL_HEIGHT =
  CHOICE_HEIGHT * NUM_TO_SHOW + CHOICE_GAP * (NUM_TO_SHOW - 1);

function InfiniteNumberPicker() {
  const id = React.useId();
  const [selectedNumber, setSelectedNumber] = React.useState(36);

  const renderedNums = range(
    selectedNumber - NUM_TO_SHOW + 1,
    selectedNumber + NUM_TO_SHOW
  );

  const isDisabled = React.useRef(false);

  React.useEffect(() => {
    isDisabled.current = true;

    const timeoutId = window.setTimeout(() => {
      isDisabled.current = false;
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [selectedNumber]);

  return (
    <>
      <Wrapper>
        <Label>
          <Prefix>let</Prefix>
          age
        </Label>

        <ArrowWrapper>
          <StraightArrow />
        </ArrowWrapper>

        <ChoicesConstrainer style={{ height: TOTAL_HEIGHT + 'rem' }}>
          <ChoicesWrapper>
            {renderedNums.map((num) => {
              const isSelected = num === selectedNumber;

              return (
                <Choice
                  key={num}
                  layout
                  animate={{
                    x: isSelected ? -16 : 0,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 100,
                    damping: 20,
                  }}
                >
                  <Shadow
                    style={{
                      opacity: isSelected ? 1 : 0.25,
                    }}
                  />
                  <ActiveBackground
                    style={{
                      opacity: isSelected ? 1 : 0,
                    }}
                  />
                  <ChoiceLabel>{num}</ChoiceLabel>
                  <Radio
                    type="radio"
                    name={`infinite-number-${id}`}
                    id={`choice-${id}-${num}`}
                    checked={isSelected}
                    onKeyDown={(event) => {
                      if (isDisabled.current) {
                        event.preventDefault();
                      }
                    }}
                    onChange={() => {
                      setSelectedNumber(num);
                    }}
                  />
                </Choice>
              );
            })}
          </ChoicesWrapper>
        </ChoicesConstrainer>
        <TopGradient />
        <BottomGradient />
      </Wrapper>
      <MobileAlternative>
        <LazyVideoGif
          includeBorder={false}
          alt="Diagram showing that the “age” variable is pointing to the number 36. The number 36 is part of a big wheel of adjacent numbers: 33, 34, 35, 36, 37, 37, 39…"
          width={1100 / 2}
          height={800 / 2}
          src="/images/the-const-deception/infinite-number-picker.mp4"
          thumbnailSrc="/images/the-const-deception/infinite-number-picker-thumbnail.png"
        />
      </MobileAlternative>
    </>
  );
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
  margin: 48px auto;
  max-width: 500px;
  isolation: isolate;
  /* Hide the numbers above/below the widget */
  overflow: hidden;
  /* Add space for the shadow on the right! */
  padding-right: 16px;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: none;
  }
`;

const Label = styled.div`
  position: relative;
  font-size: 1.25rem;
  font-family: var(--font-family-mono);
`;

const Prefix = styled.span`
  position: absolute;
  top: 5px;
  left: 0;
  transform: translateY(-100%);
  font-size: 0.75rem;
  color: var(--color-gray-700);
`;

const ArrowWrapper = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  align-self: stretch;
  padding-right: 32px;
`;

const ChoicesConstrainer = styled.div`
  display: flex;
  align-items: center;
`;

const ChoicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${CHOICE_GAP}rem;
  width: 100px;
`;

const Choice = styled(motion.label)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${CHOICE_HEIGHT}rem;
  padding: 0px 18px;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: var(--font-family-mono);
  white-space: pre-wrap;
`;

const Shadow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.1),
    0px 2px 4px hsl(0deg 0% 0% / 0.1),
    0px 4px 8px hsl(0deg 0% 0% / 0.1),
    0px 8px 16px hsl(0deg 0% 0% / 0.1);
  transition: opacity 250ms;
  will-change: opacity;
`;

const ActiveBackground = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: hsl(50deg 100% 88%);
  transition: opacity 250ms;
  will-change: opacity;
`;

const ChoiceLabel = styled.div`
  position: relative;
`;

const Radio = styled.input`
  position: absolute;
  inset: 0;
  appearance: none;
  cursor: pointer;
  border-radius: inherit;
`;

const Gradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  height: ${CHOICE_HEIGHT * 1.5}rem;
  background: linear-gradient(
    to var(--direction),
    transparent,
    var(--color-background)
  );
  pointer-events: none;
`;

const TopGradient = styled(Gradient)`
  top: 0;
  --direction: top;
`;
const BottomGradient = styled(Gradient)`
  bottom: 0;
  --direction: bottom;
`;

const MobileAlternative = styled.div`
  display: none;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: block;
  }
`;

export default InfiniteNumberPicker;
